import { useState, useRef, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Mail, Paperclip, X, Send, AlertCircle, Save } from "lucide-react";
import {
  RichTextEditor,
  RichTextEditorRef,
} from "@/components/editors/RichTextEditor";
import { ProjectAvailability } from "@/hooks/useProjectPeople";
import {
  CrewEmail,
  useSendCrewEmail,
  useSaveDraft,
} from "@/hooks/useCrewEmails";
import { cn } from "@/lib/utils";

interface SendCrewEmailDialogProps {
  availability: ProjectAvailability[];
  projectName: string;
  projectId: string;
  /** When resending a past email, pre-fill subject/body and mark already-sent recipients */
  resendEmail?: CrewEmail;
  /** When editing an existing draft */
  draftEmail?: CrewEmail;
  trigger?: React.ReactNode;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MAX_TOTAL_SIZE = 10 * 1024 * 1024; // 10 MB

export function SendCrewEmailDialog({
  availability,
  projectName,
  projectId,
  resendEmail,
  draftEmail,
  trigger,
}: SendCrewEmailDialogProps) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [selectedPersonIds, setSelectedPersonIds] = useState<Set<string>>(
    new Set(),
  );
  const [files, setFiles] = useState<File[]>([]);
  const editorRef = useRef<RichTextEditorRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sendEmail = useSendCrewEmail();
  const saveDraft = useSaveDraft();

  // People who already received the resend email
  const alreadySentPersonIds = useMemo(() => {
    if (!resendEmail?.recipients) return new Set<string>();
    return new Set(resendEmail.recipients.map((r) => r.person_id));
  }, [resendEmail]);

  // Only booked crew members with their email status
  const crewMembers = useMemo(
    () =>
      availability
        .filter((a) => a.person && a.status === "booked")
        .map((a) => ({
          personId: a.person_id,
          name: `${a.person!.first_name} ${a.person!.last_name}`,
          email: a.person!.email,
          hasEmail: !!a.person!.email,
          alreadySent: alreadySentPersonIds.has(a.person_id),
        })),
    [availability, alreadySentPersonIds],
  );

  // Initialize selection when dialog opens
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        if (draftEmail) {
          // Editing a draft: restore saved selection
          const draftIds = new Set(draftEmail.draft_person_ids ?? []);
          setSelectedPersonIds(draftIds);
          setSubject(draftEmail.subject ?? "");
        } else {
          // Pre-select all crew members with emails who haven't already received it
          const initialSelection = new Set(
            crewMembers
              .filter((m) => m.hasEmail && !m.alreadySent)
              .map((m) => m.personId),
          );
          setSelectedPersonIds(initialSelection);
          setSubject(resendEmail?.subject ?? "");
        }
        setFiles([]);
      }
      setOpen(isOpen);
    },
    [crewMembers, resendEmail, draftEmail],
  );

  const togglePerson = (personId: string) => {
    setSelectedPersonIds((prev) => {
      const next = new Set(prev);
      if (next.has(personId)) {
        next.delete(personId);
      } else {
        next.add(personId);
      }
      return next;
    });
  };

  const selectableMembers = crewMembers.filter(
    (m) => m.hasEmail && !m.alreadySent,
  );

  const toggleAll = () => {
    if (selectedPersonIds.size === selectableMembers.length) {
      setSelectedPersonIds(new Set());
    } else {
      setSelectedPersonIds(new Set(selectableMembers.map((m) => m.personId)));
    }
  };

  const handleFileAdd = (newFiles: FileList | null) => {
    if (!newFiles) return;
    setFiles((prev) => [...prev, ...Array.from(newFiles)]);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const totalFileSize = files.reduce((sum, f) => sum + f.size, 0);
  const fileSizeExceeded = totalFileSize > MAX_TOTAL_SIZE;

  const selectedRecipients = crewMembers.filter(
    (m) => m.hasEmail && selectedPersonIds.has(m.personId),
  );

  const canSend =
    selectedRecipients.length > 0 &&
    subject.trim().length > 0 &&
    !fileSizeExceeded &&
    !sendEmail.isPending &&
    !saveDraft.isPending;

  const canSaveDraft = !sendEmail.isPending && !saveDraft.isPending;

  const handleSend = async () => {
    const htmlBody = editorRef.current?.getHTML() || "";
    if (!htmlBody || htmlBody === "<p></p>") {
      return;
    }

    await sendEmail.mutateAsync({
      draft_id: draftEmail?.id,
      project_id: projectId,
      subject,
      html_body: htmlBody,
      person_ids: selectedRecipients.map((m) => m.personId),
      files,
    });

    setOpen(false);
  };

  const handleSaveDraft = async () => {
    const htmlBody = editorRef.current?.getHTML() || "";

    await saveDraft.mutateAsync({
      id: draftEmail?.id,
      project_id: projectId,
      subject,
      html_body: htmlBody,
      person_ids: Array.from(selectedPersonIds),
    });

    setOpen(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFileAdd(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const noEmailCount = crewMembers.filter((m) => !m.hasEmail).length;
  const alreadySentCount = crewMembers.filter((m) => m.alreadySent).length;

  const isEditing = !!draftEmail;
  const dialogTitle = isEditing
    ? "Edit Draft"
    : resendEmail
      ? "Resend Email to New Crew"
      : "Send Email to Crew";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <Mail className="h-4 w-4" />
            Send Email
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {/* Recipients */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>
                Recipients ({selectedPersonIds.size} of{" "}
                {selectableMembers.length} selected)
              </Label>
              {selectableMembers.length > 0 && (
                <Button variant="ghost" size="sm" onClick={toggleAll}>
                  {selectedPersonIds.size === selectableMembers.length
                    ? "Deselect All"
                    : "Select All"}
                </Button>
              )}
            </div>
            <ScrollArea className="h-[140px] border rounded-md p-2">
              <div className="space-y-1">
                {crewMembers.map((member) => {
                  const disabled = !member.hasEmail || member.alreadySent;
                  return (
                    <div
                      key={member.personId}
                      className={cn(
                        "flex items-center gap-2 py-1 px-1 rounded",
                        disabled && "opacity-50",
                      )}
                    >
                      <Checkbox
                        checked={selectedPersonIds.has(member.personId)}
                        onCheckedChange={() => togglePerson(member.personId)}
                        disabled={disabled}
                      />
                      <span className="text-sm flex-1">{member.name}</span>
                      {!member.hasEmail && (
                        <Badge
                          variant="outline"
                          className="text-xs text-muted-foreground"
                        >
                          No email
                        </Badge>
                      )}
                      {member.alreadySent && (
                        <Badge variant="secondary" className="text-xs">
                          Already sent
                        </Badge>
                      )}
                      {member.hasEmail && !member.alreadySent && (
                        <span className="text-xs text-muted-foreground">
                          {member.email}
                        </span>
                      )}
                    </div>
                  );
                })}
                {crewMembers.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No crew members added to this project.
                  </p>
                )}
              </div>
            </ScrollArea>
            {noEmailCount > 0 && !resendEmail && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {noEmailCount} crew member(s) without email addresses will be
                skipped.
              </p>
            )}
            {alreadySentCount > 0 && resendEmail && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {alreadySentCount} crew member(s) already received this email.
              </p>
            )}
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="email-subject">Subject</Label>
            <Input
              id="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={`Update from ${projectName}`}
            />
          </div>

          {/* Body */}
          <div className="space-y-2">
            <Label>Message</Label>
            <RichTextEditor
              ref={editorRef}
              content={draftEmail?.html_body ?? resendEmail?.html_body ?? ""}
              placeholder="Write your email message..."
              className="min-h-[200px]"
            />
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <Label>Attachments</Label>
            <div
              className={cn(
                "border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors",
                "hover:border-primary/50 hover:bg-muted/30",
                fileSizeExceeded && "border-destructive",
              )}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <Paperclip className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Click or drag files to attach
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => handleFileAdd(e.target.files)}
              />
            </div>

            {files.length > 0 && (
              <div className="space-y-1">
                {files.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center gap-2 text-sm bg-muted/50 rounded px-2 py-1"
                  >
                    <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate">{file.name}</span>
                    <span className="text-muted-foreground shrink-0">
                      {formatFileSize(file.size)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5"
                      onClick={() => removeFile(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{files.length} file(s)</span>
                  <span
                    className={
                      fileSizeExceeded ? "text-destructive font-medium" : ""
                    }
                  >
                    {formatFileSize(totalFileSize)} /{" "}
                    {formatFileSize(MAX_TOTAL_SIZE)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={handleSaveDraft}
            disabled={!canSaveDraft}
            className="gap-2"
          >
            {saveDraft.isPending ? (
              "Saving..."
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Draft
              </>
            )}
          </Button>
          <Button onClick={handleSend} disabled={!canSend} className="gap-2">
            {sendEmail.isPending ? (
              "Sending..."
            ) : (
              <>
                <Send className="h-4 w-4" />
                Send to {selectedRecipients.length} Recipient(s)
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
